import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listExamConfigs } from "../../api/examApi";
import { updateGrandFinalWeights } from "../../api/examConfigApi";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");
const GRAND_FINAL_DEFAULT_ID = 99;

/**
 * Direct port of digicampusbd's `exam-grandfinal-mark-configuration`
 * ("Combined Exam Setup") — for a class, decide which of its non-Grand-Final
 * exams (1st/2nd/3rd semester, etc.) feed into the combined "Grand Final"
 * result, and what percentage weight + serial order each contributes.
 * Reuses the `percentage`/`examSerial` fields already present on
 * `ExamConfiguration` (confirmed via a live read: institute 1's real 1st/
 * 2nd/3rd semester configs already carry real 33/34/33% weights) — only a
 * new bulk-update endpoint was needed, not a new entity.
 * <p>
 * This is the real prerequisite for `exam-result-process-grand-final` and
 * its dependent marksheet/tabulation-sheet urls, which stay deferred (see
 * MIGRATION.md): the old app's actual combination engine is a ~988-line,
 * 4-variant computation (`GrandFinalResultProcessService`) — a materially
 * larger undertaking than anything else built in this category, comparable
 * in scope to the already-deferred `exam-merit-position-generate`.
 */
export default function GrandFinalMarkConfigurationPage() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    classApi.list().then(setClasses);
  }, []);

  const loadConfigs = async (id) => {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const configs = await listExamConfigs(id);
      setRows(
        configs
          .filter((c) => c.examDefaultId !== GRAND_FINAL_DEFAULT_ID)
          .map((c) => ({
            examConfigurationId: c.examConfigId,
            examName: c.examName,
            percentage: c.percentage,
            examSerial: c.examSerial || "",
          }))
      );
    } catch (err) {
      setError("Failed to load exam configurations for this class.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    setClassId(id);
    loadConfigs(id);
  };

  const updateRow = (examConfigurationId, field, value) => {
    setRows((prev) => prev.map((r) => (r.examConfigurationId === examConfigurationId ? { ...r, [field]: value } : r)));
  };

  const handleUpdate = async () => {
    setError("");
    setSuccess("");
    const total = rows.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
    if (total !== 100) {
      setError("Total percentage should be equal to 100");
      return;
    }
    // A 0%-weighted exam (e.g. "Annual Exam" in real institute-1 data) doesn't
    // participate in the Grand Final at all, so it's never been given a real
    // serial either — only require one for exams that actually contribute.
    if (rows.some((r) => Number(r.percentage) > 0 && !r.examSerial)) {
      setError("Please Select Exam Serial");
      return;
    }
    setSaving(true);
    try {
      await updateGrandFinalWeights(
        rows.map((r) => ({
          examConfigurationId: r.examConfigurationId,
          examSerial: r.examSerial ? Number(r.examSerial) : 0,
          percentage: Number(r.percentage),
        }))
      );
      setSuccess("Grand final weights updated");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Combined Exam Setup</h1>
      </div>

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      <div className="table-scroll">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>SL</th>
              <th>Class</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {classes.map((c, i) => (
              <tr key={c.id}>
                <td>{i + 1}</td>
                <td>{c.name}</td>
                <td>
                  <button type="button" className="btn-secondary" onClick={() => handleEdit(c.id)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {classId && (
        <div style={{ marginTop: 24 }}>
          <h2>Grand Final Mark Configuration — {classes.find((c) => String(c.id) === String(classId))?.name}</h2>
          {loading ? (
            <p>Loading...</p>
          ) : rows && rows.length > 0 ? (
            <>
              <div className="table-scroll">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>Exam Name</th>
                      <th>Percentage</th>
                      <th>Exam Serial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.examConfigurationId}>
                        <td>{r.examName}</td>
                        <td>
                          <input
                            type="number"
                            value={r.percentage}
                            onChange={(e) => updateRow(r.examConfigurationId, "percentage", e.target.value)}
                          />
                        </td>
                        <td>
                          <select value={r.examSerial} onChange={(e) => updateRow(r.examConfigurationId, "examSerial", e.target.value)}>
                            <option value=""></option>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={handleUpdate} disabled={saving}>
                  {saving ? "Updating..." : "Update"}
                </button>
              </div>
            </>
          ) : (
            <p>No non-Grand-Final exams configured for this class.</p>
          )}
        </div>
      )}
    </MainLayout>
  );
}
